<template>
  <div class="template-picker">
    <!-- 類型篩選 chips -->
    <div class="filter-section">
      <div class="filter-label">篩選類型</div>
      <div class="type-filter-row">
        <button
          v-for="t in MSG_TYPE_FILTERS"
          :key="t.value"
          type="button"
          class="type-chip"
          :class="{ active: typeFilter === t.value }"
          @click="typeFilter = t.value"
        >
          <i :class="t.icon"></i>
          {{ t.label }}
        </button>
      </div>
    </div>

    <!-- 搜尋 + 範本列表 -->
    <div class="list-section">
      <div class="template-search-box">
        <i class="ri-search-line"></i>
        <input
          v-model="searchKeyword"
          type="text"
          placeholder="搜尋範本名稱..."
          class="template-search-input"
        />
        <button v-if="searchKeyword" type="button" class="search-clear-btn" @click="searchKeyword = ''">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <div v-if="loading" class="templates-loading">
        <i class="ri-loader-4-line spin"></i> 載入範本中...
      </div>

      <div v-else class="template-list">
        <div
          v-for="t in filteredTemplates"
          :key="t.id"
          class="template-item"
          :class="{ selected: modelValue && modelValue.id === t.id, disabled }"
          @click="select(t)"
        >
          <div class="template-item-icon">
            <i :class="msgTypeIcon(t.messageType)"></i>
          </div>
          <div class="template-item-info">
            <div class="template-item-name">{{ t.name }}</div>
            <div class="template-item-meta">
              <span class="type-badge" :class="`type-${t.messageType}`">{{ msgTypeLabel(t.messageType) }}</span>
              <span
                v-if="_isBroadcastOrUnlabeledAudience(t)"
                class="audience-tag audience-broadcast"
              >群發</span>
              <span
                v-else
                class="audience-tag audience-personal"
              >個人</span>
              <span v-if="t.description" class="template-item-desc">{{ t.description }}</span>
            </div>
          </div>
          <i v-if="modelValue && modelValue.id === t.id" class="ri-checkbox-circle-fill selected-check"></i>
        </div>

        <div v-if="!loading && filteredTemplates.length === 0" class="templates-empty">
          <i class="ri-file-search-line"></i>
          <span>{{ emptyMessage }}</span>
        </div>
      </div>
    </div>

    <!-- 已選摘要 -->
    <div v-if="modelValue" class="selected-summary">
      <i class="ri-check-double-line"></i>
      <div class="selected-summary-info">
        <div class="selected-summary-label">已選擇範本</div>
        <div class="selected-summary-name">{{ modelValue.name }}</div>
      </div>
      <button type="button" class="btn-clear" @click="clear">
        <i class="ri-close-line"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';

const MSG_TYPE_FILTERS = [
  { value: '', label: '全部', icon: 'ri-apps-line' },
  { value: 'text', label: '文字', icon: 'ri-text' },
  { value: 'image', label: '圖片', icon: 'ri-image-line' },
  { value: 'video', label: '影片', icon: 'ri-video-line' },
  { value: 'image_carousel', label: '圖片輪播', icon: 'ri-gallery-line' },
  { value: 'imagemap', label: '圖文', icon: 'ri-collage-line' },
  { value: 'flex', label: 'Flex', icon: 'ri-layout-3-line' },
  { value: 'flex_carousel', label: 'Flex 輪播', icon: 'ri-layout-grid-line' }
];

const MSG_TYPE_LABEL = {
  text: '文字', image: '圖片', video: '影片',
  flex: 'Flex', flex_carousel: 'Flex 輪播',
  image_carousel: '圖片輪播', imagemap: '圖文訊息'
};

const MSG_TYPE_ICON = {
  text: 'ri-text', image: 'ri-image-line', video: 'ri-video-line',
  flex: 'ri-layout-3-line', flex_carousel: 'ri-layout-grid-line',
  image_carousel: 'ri-gallery-line', imagemap: 'ri-collage-line'
};

export default {
  name: 'LineMsgTemplatePicker',
  props: {
    // v-model：傳入完整 template 物件（null 表示未選）
    modelValue: { type: Object, default: null },
    // 初始 ID（用於從已儲存設定還原選取狀態，modelValue 優先）
    selectedId: { type: String, default: null },
    /** null：不篩選；personal：僅個人；broadcast：群發（後端含 audience_type=broadcast 或 NULL） */
    audienceFilter: { type: String, default: null, validator: (v) => v == null || v === '' || v === 'personal' || v === 'broadcast' },
    /** 已選過的範本 ID，從清單隱藏（給「點一下就加入」的多選頁使用） */
    excludeIds: { type: Array, default: () => [] },
    /** 為 true 時不可再點選，例如已達則數上限 */
    disabled: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'loaded'],
  data() {
    return {
      MSG_TYPE_FILTERS,
      loading: false,
      templateList: [],
      typeFilter: '',
      searchKeyword: ''
    };
  },
  computed: {
    filteredTemplates() {
      let list = this.templateList;
      if (this.audienceFilter === 'personal') {
        list = list.filter(t => this._rawAudienceType(t) === 'personal');
      } else if (this.audienceFilter === 'broadcast') {
        list = list.filter(t => this._isBroadcastOrUnlabeledAudience(t));
      }
      if (this.typeFilter) {
        list = list.filter(t => t.messageType === this.typeFilter);
      }
      if (this.searchKeyword.trim()) {
        const q = this.searchKeyword.trim().toLowerCase();
        list = list.filter(t => t.name.toLowerCase().includes(q));
      }
      if (this.excludeIds.length) {
        const excluded = new Set(this.excludeIds);
        list = list.filter(t => !excluded.has(t.id));
      }
      return list;
    },
    emptyMessage() {
      if (this.templateList.length === 0) return '尚無可用範本';
      const excluded = new Set(this.excludeIds);
      const allPicked = this.templateList.every((template) => excluded.has(template.id));
      if (allPicked && !this.typeFilter && !this.searchKeyword.trim()) {
        return '可挑選的範本都已加入';
      }
      return '沒有符合的範本';
    }
  },
  watch: {
    audienceFilter() {
      this.loadTemplates();
    },
    // 若 selectedId 在載入後才傳入，補做還原
    selectedId(newId) {
      if (newId && !this.modelValue && !this.loading) {
        this._restoreFromId(newId);
      }
    }
  },
  mounted() {
    this.loadTemplates();
  },
  methods: {
    async loadTemplates() {
      this.loading = true;
      try {
        if (this.audienceFilter === 'broadcast') {
          let res = await lineMsgTemplateService.list({
            pageSize: 100,
            status: 'active',
            audienceType: 'broadcast'
          });
          let items = this._extractListItems(res).filter(t => this._isBroadcastOrUnlabeledAudience(t));
          if (items.length === 0) {
            res = await lineMsgTemplateService.list({ pageSize: 100, status: 'active' });
            items = this._extractListItems(res).filter(t => this._isBroadcastOrUnlabeledAudience(t));
          }
          this.templateList = items;
        } else {
          const listParams = { pageSize: 100, status: 'active' };
          if (this.audienceFilter === 'personal') {
            listParams.audienceType = 'personal';
          }
          const res = await lineMsgTemplateService.list(listParams);
          this.templateList = this._extractListItems(res);
        }
        this.$emit('loaded', this.templateList);
        // 載入完成後，若有 selectedId 且尚未選取，自動還原
        if (this.selectedId && !this.modelValue) {
          this._restoreFromId(this.selectedId);
        }
      } catch (e) {
        console.error('載入訊息範本失敗:', e);
      } finally {
        this.loading = false;
      }
    },

    _extractListItems(res) {
      if (res?.success && res.data && Array.isArray(res.data.items)) {
        return res.data.items;
      }
      if (res?.data && Array.isArray(res.data.items)) {
        return res.data.items;
      }
      return [];
    },

    _rawAudienceType(t) {
      if (!t || typeof t !== 'object') return undefined;
      const a = t.audienceType ?? t.audience_type;
      return a === '' ? undefined : a;
    },

    /** 群發推播用：後端 audience_type = broadcast 或 NULL／未帶欄位（舊資料） */
    _isBroadcastOrUnlabeledAudience(t) {
      const a = this._rawAudienceType(t);
      if (a === 'personal') return false;
      if (a === 'broadcast') return true;
      return a == null;
    },

    _restoreFromId(idOrCode) {
      const found = this.templateList.find(
        t => t.id === idOrCode || t.templateCode === idOrCode
      );
      if (found) this.$emit('update:modelValue', found);
    },

    select(t) {
      if (this.disabled) return;
      this.$emit('update:modelValue', t);
    },

    clear() {
      this.$emit('update:modelValue', null);
    },

    // 供外部呼叫：根據 id 或 templateCode 還原選取狀態
    async restoreById(idOrCode) {
      if (!idOrCode) return;
      if (this.loading) {
        await new Promise(resolve => {
          const stop = this.$watch('loading', val => {
            if (!val) { stop(); resolve(); }
          });
        });
      }
      this._restoreFromId(idOrCode);
    },

    msgTypeLabel(type) {
      return MSG_TYPE_LABEL[type] || type;
    },

    msgTypeIcon(type) {
      return MSG_TYPE_ICON[type] || 'ri-file-line';
    }
  }
};
</script>

<style scoped>
.template-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 類型篩選 */
.filter-section {}
.filter-label {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}
.type-filter-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.type-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  background: #fff;
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.type-chip i { font-size: 13px; }
.type-chip:hover { border-color: #93c5fd; color: #3b82f6; }
.type-chip.active {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #3b82f6;
  font-weight: 600;
}

/* 搜尋 */
.list-section {}
.template-search-box {
  position: relative;
  margin-bottom: 8px;
}
.template-search-box > i {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 14px;
  pointer-events: none;
}
.template-search-input {
  width: 100%;
  padding: 7px 32px 7px 32px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  box-sizing: border-box;
  outline: none;
}
.template-search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
.search-clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 14px;
  padding: 2px;
  line-height: 1;
}
.search-clear-btn:hover { color: #6b7280; }

/* 範本列表 */
.template-list {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.template-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.12s;
}
.template-item:last-child { border-bottom: none; }
.template-item:hover { background: #f9fafb; }
.template-item.selected { background: #eff6ff; }
.template-item.disabled,
.template-item.disabled:hover {
  background: #fff;
  cursor: not-allowed;
  opacity: 0.6;
}

.template-item-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  color: #6b7280;
  flex-shrink: 0;
}
.template-item.selected .template-item-icon {
  background: #dbeafe;
  color: #3b82f6;
}

.template-item-info { flex: 1; min-width: 0; }
.template-item-name {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.template-item-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
}

.type-badge {
  font-size: 10px;
  padding: 1px 7px;
  border-radius: 10px;
  font-weight: 600;
  background: #f3f4f6;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
}
.type-badge.type-text     { background: #fef3c7; color: #b45309; }
.type-badge.type-image    { background: #dcfce7; color: #15803d; }
.type-badge.type-video    { background: #fce7f3; color: #be185d; }
.type-badge.type-flex,
.type-badge.type-flex_carousel { background: #ede9fe; color: #7c3aed; }
.type-badge.type-image_carousel { background: #e0f2fe; color: #0369a1; }
.type-badge.type-imagemap { background: #ffedd5; color: #c2410c; }

.audience-tag {
  font-size: 9px;
  padding: 1px 6px;
  border-radius: 8px;
  font-weight: 600;
  flex-shrink: 0;
}
.audience-tag.audience-personal { background: #d1fae5; color: #047857; }
.audience-tag.audience-broadcast { background: #fef3c7; color: #b45309; }

.template-item-desc {
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-check { font-size: 20px; color: #3b82f6; flex-shrink: 0; }

.templates-loading {
  padding: 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
}
.templates-empty {
  padding: 24px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.templates-empty i { font-size: 24px; }

/* 已選摘要 */
.selected-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
}
.selected-summary > i { font-size: 20px; color: #16a34a; flex-shrink: 0; }
.selected-summary-info { flex: 1; min-width: 0; }
.selected-summary-label { font-size: 11px; color: #4ade80; }
.selected-summary-name {
  font-size: 13px;
  font-weight: 600;
  color: #15803d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.btn-clear {
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 16px;
  padding: 2px;
  border-radius: 4px;
  flex-shrink: 0;
}
.btn-clear:hover { color: #ef4444; }

/* Loading spinner */
.spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
